import ExportModal from "../components/session/export/ExportModal";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getFullTranscriptionByIdAsync } from "../redux/transcription";
import { pushUndo, buildUndoEntry } from "../redux/undo";
import SessionView from "../components/session/SessionView";
import { useFooter } from "../components/layout/FooterContext";
import AppButtonFooter from "../components/common/AppButtonFooter";
import { generateDocFromFrontendAsync, exportSpeakerSectionAsync, rawTranscriptionDownloadAsync, rtfDownloadAsync } from "../redux/transcription";
import SpeakersModal from "../components/session/SpeakersModal";
import { editParagraphAsync } from "../redux/paragraph";
import { distributeWordsToParagraphs } from "../utils/wordDistribution";
import useBeingTranscribed, { askBeingTranscribed } from "../hooks/useBeingTranscribed";
import { addToast } from "@heroui/react";

// Global store for selected speakers per session (using window object)
if (!window.selectedSpeakersStore) {
  window.selectedSpeakersStore = new Map();
}

const Session = ({ sessionIdOverride, isEmbedded = false, isActive = true }) => {
  const [openExport, setOpenExport] = useState(false);
  const { id: idFromParams } = useParams();
  const id = sessionIdOverride || idFromParams;
  const dispatch = useDispatch();
  const lastFetchedIdRef = useRef(null);
  const prevSessionIdRef = useRef(null);
  // Initialize selectedSpeakers from global store if available
  const [selectedSpeakers, setSelectedSpeakers] = useState(() => {
    // Initialize store if needed
    if (!window.selectedSpeakersStore) {
      window.selectedSpeakersStore = new Map();
    }
    if (!id) return new Set();
    const existingSpeakers = window.selectedSpeakersStore.get(String(id));
    const initialSet = existingSpeakers ? new Set(existingSpeakers) : new Set();
    return initialSet;
  });
  // Initialize ref with the initial selectedSpeakers value
  const prevSelectedSpeakersRef = useRef(new Set(selectedSpeakers));
  const [groupByHeading, setGroupByHeading] = useState(null);
  const { setFooterContent, setTitle, setHeader } = useFooter();
  const [filename, setFilename] = useState(null)
  const [selectedHeadings, setSelectedHeadings] = useState(new Set());
  const [filteredData, setFilteredData] = useState([]);
  const [navData, setNavData] = useState([]);
  const [selectedGroupBy, setSelectedGroupBy] = useState(new Set(["Heading 2"]));
  const [headingsRegions, setHeadingRegions] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [headingOptionsState, setHeadingOptionsState] = useState([]);
  const [expandAllToken, setExpandAllToken] = useState(0);
  const [areAllOpen, setAreAllOpen] = useState(true);
  const [isSpeakersOpen, setIsSpeakersOpen] = useState(false);
  const [navRefresh, setNavRefresh] = useState(0);
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchIndex, setSearchIndex] = useState(-1);
  const [searchTarget, setSearchTarget] = useState(null); // { sectionIndex, paragraphRefIndex }
  const searchResultsRef = useRef([]);
  const [searchJumpCount, setSearchJumpCount] = useState(0);
  // Prompt mode state: "hard" or "soft"
  const [promptMode, setPromptMode] = useState("soft");
  const recentSessionsUpdatedRef = useRef(false);

  useEffect(() => {
    searchResultsRef.current = searchResults;
  }, [searchResults]);

  useEffect(() => {
    setTitle(null);
    setHeader(null);
  }, [setTitle, setHeader]);

  const handleSearchNext = useCallback(() => {
    const total = searchResultsRef.current.length;
    if (!total) return;
    setSearchIndex((prev) => {
      const next = ((prev ?? -1) + 1) % total;
      setSearchTarget(searchResultsRef.current[next]);
      setSearchJumpCount((c) => c + 1);
      return next;
    });
  }, []);

  const handleSearchPrev = useCallback(() => {
    const total = searchResultsRef.current.length;
    if (!total) return;
    setSearchIndex((prev) => {
      const next = (prev <= 0 ? total - 1 : prev - 1);
      setSearchTarget(searchResultsRef.current[next]);
      setSearchJumpCount((c) => c + 1);
      return next;
    });
  }, []);

  const handleToggleFilters = useCallback(() => {
    setFiltersExpanded((prev) => !prev);
  }, [setFiltersExpanded]);

  const handleToggleAllSpeakers = useCallback(() => {
    setAreAllOpen((prev) => {
      const next = !prev;
      setExpandAllToken((c) => c + 1);
      return next;
    });
  }, [setAreAllOpen, setExpandAllToken]);

  const fullTranscription = useSelector((state) => {
    // Use the transcription from the map by ID for multi-screen support
    return state.transcriptions.fullTranscriptionsById[id] || state.transcriptions.fullTranscription;
  });
  const loading = useSelector((state) => state.transcriptions.loading);
  const error = useSelector((state) => state.transcriptions.error);
  // While the session is transcribed again, the backend refuses changes to
  // its transcript and replaces it at the end: the editor stays read-only,
  // and until it has reloaded a transcript replaced meanwhile.
  const reloadTranscript = useCallback(() => {
    if (id) dispatch(getFullTranscriptionByIdAsync(id));
  }, [id, dispatch]);
  // Found locked while open: most saves do not report a refusal, so say it.
  const warnLocked = useCallback(() => {
    addToast({
      title: "Sitzung wird neu transkribiert",
      description:
        "Bearbeiten ist bis zum Ende gesperrt. Änderungen der letzten Sekunden wurden eventuell nicht gespeichert.",
      color: "warning",
    });
  }, []);
  const loadedHere = String(fullTranscription?.id) === String(id);
  const lock = useBeingTranscribed(
    id,
    loadedHere && Boolean(fullTranscription?.being_transcribed),
    loadedHere ? fullTranscription?.transcript_version ?? null : null,
    askBeingTranscribed,
    reloadTranscript,
    warnLocked
  );
  const readOnly = lock !== null;
  useEffect(() => {
    if (readOnly) setIsSpeakersOpen(false);
  }, [readOnly]);
  // The transcript version on screen. The view is built anew for another
  // one: its rows were all replaced, and a paragraph component left over
  // from the old ones would save its old text into the new row at its place.
  const [shownVersion, setShownVersion] = useState(null);
  const modalSpeakers = useMemo(() => {
    const paragraphSource =
      Array.isArray(groupByHeading) && groupByHeading.length > 0
        ? groupByHeading.flatMap((section) =>
            (section?.children || []).filter((child) => child?.type === "paragraph")
          )
        : (fullTranscription?.paragraphs || []);

    const seen = new Set();
    const list = [];
    paragraphSource.forEach((p) => {
      const speakerNumber = Number.isFinite(Number(p.speaker_number))
        ? Number(p.speaker_number)
        : undefined;
      const normalizedName = String(p.speaker_name ?? "").trim();
      const hasSpeakerName =
        normalizedName.length > 0 && normalizedName.toLowerCase() !== "no";
      const key = `${hasSpeakerName ? normalizedName : "no"}::${speakerNumber ?? ""}`;
      if (!seen.has(key)) {
        list.push({
          key,
          label: hasSpeakerName ? normalizedName : "Speaker",
          value: speakerNumber,
        });
        seen.add(key);
      }
    });
    return list;
  }, [fullTranscription, groupByHeading]);

  function parseDurationToSeconds(isoDuration) {
    const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/;
    const match = isoDuration.match(regex);
  
    if (!match) return 0;
  
    const hours = parseInt(match[1] || "0", 10);
    const minutes = parseInt(match[2] || "0", 10);
    const seconds = parseFloat(match[3] || "0");
  
    return hours * 3600 + minutes * 60 + seconds;
  }

  const normalizeToken = (value) => {
    if (!value) return "";
    return String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/gi, "")
      .toLowerCase();
  };

  const getParagraphWordEntries = (paragraph) => {
    const list = Array.isArray(paragraph?.paragraph_words) ? paragraph.paragraph_words : [];
    return list
      .map((word) => {
        const offset =
          typeof word?.offsetSeconds === "number"
            ? word.offsetSeconds
            : parseDurationToSeconds(word?.word_offset);
        const normalized = normalizeToken(word?.word);
        if (!Number.isFinite(offset) || !normalized) return null;
        return { normalized, offset };
      })
      .filter(Boolean);
  };

  const countOccurrencesBeforeIndex = (text, normalizedToken, untilIndex) => {
    if (!text || !normalizedToken) return 0;
    const regex = /\S+/g;
    let count = 0;
    let match;
    while ((match = regex.exec(text))) {
      if (match.index >= Math.max(0, untilIndex)) break;
      const token = normalizeToken(match[0]);
      if (token === normalizedToken) count += 1;
    }
    return count;
  };

  const createCharIndexToWordOffsetResolver = (paragraph, sourceText) => {
    if (!paragraph || !sourceText) return null;
    const regex = /\S+/g;
    const tokens = [];
    let match;
    while ((match = regex.exec(sourceText))) {
      const normalized = normalizeToken(match[0]);
      if (!normalized) continue;
      tokens.push({
        normalized,
        start: match.index,
        end: match.index + match[0].length,
        order: tokens.length,
        wordIndex: null,
      });
    }
    if (tokens.length === 0) return null;
    const wordEntries = getParagraphWordEntries(paragraph);
    if (wordEntries.length === 0) return null;
    let entryIdx = 0;
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      while (entryIdx < wordEntries.length) {
        const entry = wordEntries[entryIdx];
        if (entry?.normalized === token.normalized) {
          token.wordIndex = entryIdx;
          entryIdx += 1;
          break;
        }
        entryIdx += 1;
      }
    }
    const findTokenForChar = (charIndex) => {
      let low = 0;
      let high = tokens.length - 1;
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const token = tokens[mid];
        if (charIndex < token.start) {
          high = mid - 1;
        } else if (charIndex >= token.end) {
          low = mid + 1;
        } else {
          return token;
        }
      }
      return null;
    };
    return (charIndex) => {
      if (typeof charIndex !== "number" || Number.isNaN(charIndex)) return null;
      const token = findTokenForChar(charIndex);
      if (!token) return null;
      const entryIndex =
        token.wordIndex != null
          ? token.wordIndex
          : Math.min(wordEntries.length - 1, token.order);
      const entry = wordEntries[entryIndex];
      if (entry && Number.isFinite(entry.offset)) {
        return entry.offset;
      }
      return null;
    };
  };

  const getJumpSecondsForSearchMatch = (paragraph, queryLower, targetOccurrence = 0) => {
    if (!paragraph) return null;
    const tokens = (queryLower || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(normalizeToken);
    const wordEntries = getParagraphWordEntries(paragraph);
    const firstToken = tokens[0];
    if (firstToken && wordEntries.length > 0) {
      const matchLength = Math.max(tokens.length, 1);
      let occurrenceCounter = 0;
      for (let i = 0; i <= wordEntries.length - matchLength; i++) {
        if (wordEntries[i].normalized !== firstToken) continue;
        let matches = true;
        for (let j = 1; j < matchLength; j++) {
          if (wordEntries[i + j].normalized !== tokens[j]) {
            matches = false;
            break;
          }
        }
        if (matches) {
          if (occurrenceCounter === targetOccurrence) {
            const targetOffset = wordEntries[i].offset;
            if (Number.isFinite(targetOffset)) return targetOffset;
          }
          occurrenceCounter += 1;
        }
      }
      const fallbackEntry = wordEntries.find((entry) => entry.normalized === firstToken);
      if (fallbackEntry && Number.isFinite(fallbackEntry.offset)) {
        return fallbackEntry.offset;
      }
    }
    const startVal = Number(paragraph.start);
    return Number.isFinite(startVal) ? startVal : null;
  };

  function getSecondsBetween(startISO, endCustom) {
    // Parse the start ISO timestamp (already in UTC)
    const startDate = new Date(startISO);
  
    // Parse the custom end format: "DD.MM.YYYY HH:mm:ss"
    const [datePart, timePart] = endCustom.split(" ");
    const [day, month, year] = datePart.split(".").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);
  
    // Interpret the custom date in the same time zone as the ISO string (UTC)
    const endDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  
    // Return the difference in seconds
    return (endDate - startDate) / 1000;
  }

  function groupByHeading2(items) {
    const sorted = items.sort((a, b) => a.start - b.start);
    const output = [];

    let currentSection = null;
    let beforeFirstHeading = [];
    const isHeadingItem = (item) => /heading/i.test(String(item?.type || ""));
    const defaultHeadingType =
      sorted.find((item) => isHeadingItem(item))?.type || "Heading 1";

    for (let i = 0; i < sorted.length; i++) {
      const item = sorted[i];

      if (isHeadingItem(item)) {
        if (currentSection) {
          output.push(currentSection);
        }
        currentSection = { heading2: item, children: [] };
      } else {
        if (!currentSection) {
          beforeFirstHeading.push(item);
        } else {
          currentSection.children.push(item);
        }
      }
    }

    // Push last section
    if (currentSection) {
      output.push(currentSection);
    }

    // Add the "Before first heading" section if needed
    if (beforeFirstHeading.length > 0) {
      output.unshift({
        heading2: {
          content: "Vor erster Überschrift",
          start: 0,
          type: "Heading 4",
        },
        children: beforeFirstHeading,
      });
    }

    // Enforce strict children time bounds
    return output.map((section, i, arr) => {
      const start = section.heading2.start;
      const nextStart = arr[i + 1]?.heading2.start ?? Infinity;

      section.children = section.children.filter(
        (child) => child.start >= start && child.start < nextStart
      );

      return section;
    });
  }


  const handleDocumentDownload = async (softSide) => {
    const payload = {
      "session_id": id,
      "filename": fullTranscription.name,
      "soft_side": softSide
    };
  
    const action = await dispatch(generateDocFromFrontendAsync(payload));
  
    if (!generateDocFromFrontendAsync.fulfilled.match(action)) {
      console.error("Error:", action.error.message);
    }
  };

  const handlePerSpeakerDownload = async (softSide) => {
    const payload = {
      "session_id": id,
      "filename": fullTranscription.name,
      "soft_side": softSide
    };
  
    const action = await dispatch(exportSpeakerSectionAsync(payload));
  
    if (!exportSpeakerSectionAsync.fulfilled.match(action)) {
      console.error("Error:", action.error.message);
    }
  };

  const handlerawTranscriptionDownload = async () => {
    const payload = {
      "session_id": id,
      "job_id":fullTranscription.batch_transcription_id,
      "start_time":fullTranscription.start,
      "filename": fullTranscription.name+"_raw"
    };
  
    const action = await dispatch(rawTranscriptionDownloadAsync(payload));
  
    if (!rawTranscriptionDownloadAsync.fulfilled.match(action)) {
      console.error("Error:", action.error.message);
    }
  };

  const handlertfDownload = async () => {
    
  
    const action = await dispatch(rtfDownloadAsync(id));
  
    if (!rtfDownloadAsync.fulfilled.match(action)) {
      console.error("Error:", action.error.message);
    }
  };


  const handleExportStartFromModal = ({ type, softSide }) => {
    if (type === "full") {
      handleDocumentDownload(softSide);
    } else if (type === "speaker") {
      handlePerSpeakerDownload(softSide);
    }
    else if (type === "rawTranscription") {
      handlerawTranscriptionDownload();
    }
    else if (type === "rtf") {
      handlertfDownload();
    }
    setOpenExport(false);
  };

  useEffect(() => {
    if(fullTranscription){
        const normalizedParagraphs = (fullTranscription?.paragraphs || []).map((p) => {
          const startSeconds = parseDurationToSeconds(p.start);
          const durationSeconds = parseDurationToSeconds(p.end);
          return {
            ...p,
            type: "paragraph",
            start: Number.isFinite(startSeconds) ? startSeconds : 0,
            duration_seconds: Number.isFinite(durationSeconds) ? durationSeconds : 0,
          };
        }).sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
        const paragraphWordsList = (fullTranscription?.paragraph_words || [])
          .map((word) => ({
            ...word,
            offsetSeconds: parseDurationToSeconds(word?.word_offset),
          }))
          .filter((word) => Number.isFinite(word.offsetSeconds))
          .sort((a, b) => a.offsetSeconds - b.offsetSeconds);
        // Distribute the transcription-level `paragraph_words` list to each
        // paragraph by TIME RANGE — the only reliable strategy when the
        // backend doesn't tag words with a paragraph_id. See unit tests at
        // src/utils/wordDistribution.test.js for the exact contract.
        // Previously the inline code here used a "count rendered words and
        // slice that many off the orphan list" approach that produced
        // 20+ second highlight offsets whenever rendered and DB word counts
        // diverged for any paragraph.
        const paragraphs = distributeWordsToParagraphs({
          paragraphs: normalizedParagraphs,
          paragraphWords: paragraphWordsList,
        });
        const headings = (fullTranscription?.headings || []).map(h => ({ ...h, start: Math.floor(getSecondsBetween(fullTranscription.start, h.start)) }));
        // Build speakers with stable number value from paragraphs
        const nameToNumber = new Map();
        for (const p of fullTranscription?.paragraphs || []) {
          if (p.speaker_name) {
            // Prefer first seen mapping
            if (!nameToNumber.has(p.speaker_name) && (p.speaker_number !== undefined && p.speaker_number !== null)) {
              nameToNumber.set(p.speaker_name, Number(p.speaker_number));
            }
          }
        }
        const speakersList = Array.from(nameToNumber.entries()).map(([name, num]) => ({
          key: name,
          label: name,
          value: Number.isFinite(num) ? num : undefined
        }));
        setSpeakers(speakersList);

        const headingRegions = (fullTranscription.headings || [])
          .filter(h => h.type === Array.from(selectedGroupBy)[0] && h.content)
          .map((h, index) => ({
            // start: h.start,
            start: Math.floor(getSecondsBetween(fullTranscription.start, h.start)),
            content: h.content
          }))
          .sort((a, b) => (a.start - b.start))
          

        setHeadingRegions(headingRegions)

        // Add "Vor erster Überschrift" option if there are paragraphs before the first heading
        const sortedItems = [...headings, ...paragraphs].sort((a, b) => a.start - b.start);
        const hasBeforeFirstHeading = sortedItems.length > 0 && !/heading/i.test(String(sortedItems[0]?.type || ""));

        // Sort consecutive heading blocks by level (lower level first), same as navigation box
        const isHeading = (item) => /heading/i.test(String(item?.type || ""));
        const headingRankNum = (t) => { const m = /(\d+)/.exec(String(t || "")); return m ? parseInt(m[1], 10) : 999; };
        const sortedWithHeadingBlocks = [];
        let i = 0;
        while (i < sortedItems.length) {
          if (isHeading(sortedItems[i])) {
            const block = [];
            while (i < sortedItems.length && isHeading(sortedItems[i])) {
              block.push(sortedItems[i]);
              i++;
            }
            block.sort((a, b) => headingRankNum(a.type) - headingRankNum(b.type));
            sortedWithHeadingBlocks.push(...block);
          } else {
            sortedWithHeadingBlocks.push(sortedItems[i]);
            i++;
          }
        }

        // Order headings by their position in the combined list (same order as navigation box)
        const seenHeadings = new Set();
        const headingOptions = sortedWithHeadingBlocks
          .filter((item) => isHeading(item) && !!item?.content && !seenHeadings.has(item.content))
          .map((h) => {
            seenHeadings.add(h.content);
            return { key: h.content, label: h.content };
          });

        if (hasBeforeFirstHeading) {
          headingOptions.unshift({ key: "Vor erster Überschrift", label: "Vor erster Überschrift" });
        }

        setHeadingOptionsState(headingOptions);
        setFilename(fullTranscription.name)

        // Clear footer content
        setFooterContent(null);


        const combined = [...headings, ...paragraphs].sort((a, b) => a.start - b.start);
        setGroupByHeading(groupByHeading2(combined))
        setShownVersion(fullTranscription.transcript_version ?? null)
    }
  }, [fullTranscription, selectedGroupBy]);

  // Track last viewed sessions (max 5)
  useEffect(() => {
    if (!fullTranscription || String(fullTranscription.id) !== String(id)) return;
    if (recentSessionsUpdatedRef.current) return;
    const entry = {
      id: String(fullTranscription.id),
      name: fullTranscription.name || `Session ${fullTranscription.id}`,
      viewedAt: new Date().toISOString(),
    };
    try {
      const stored = localStorage.getItem("recentSessions");
      const parsed = stored ? JSON.parse(stored) : [];
      const next = Array.isArray(parsed) ? parsed.filter((s) => String(s.id) !== entry.id) : [];
      next.unshift(entry);
      const trimmed = next.slice(0, 5);
      localStorage.setItem("recentSessions", JSON.stringify(trimmed));
      window.dispatchEvent(new CustomEvent("recent-sessions-updated"));
      recentSessionsUpdatedRef.current = true;
    } catch (e) {
      console.warn("[Session] Failed to persist recentSessions:", e);
    }
  }, [fullTranscription, id]);

  // Build search results whenever query or filtered data changes
  useEffect(() => {
    if (!searchQuery || !Array.isArray(filteredData)) {
      setSearchResults([]);
      setSearchIndex(-1);
      setSearchTarget(null);
      return;
    }
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      setSearchIndex(-1);
      setSearchTarget(null);
      return;
    }
    const q = trimmedQuery.toLowerCase();
    const selectedSpeakerKeys = Array.from(selectedSpeakers || []);
    const results = [];
    filteredData.forEach((section, sectionIndex) => {
      let paragraphCounter = 0;
      section.children.forEach((child, childIndex) => {
        if (child?.type === "paragraph") {
          paragraphCounter += 1;
          const sourceText =
            child.raw_content ||
            child.corrected_content ||
            child.original_content ||
            "";
          const text = sourceText.toLowerCase();
          if (!text) return;
          if (selectedSpeakerKeys.length > 0) {
            if (!selectedSpeakerKeys.includes(child.speaker_name)) return;
          }
          if (text.includes(q)) {
            const charIndexResolver = createCharIndexToWordOffsetResolver(child, text);
            const firstTokenRaw = (q.split(/\s+/)[0] || "");
            const normalizedFirstToken = normalizeToken(firstTokenRaw);
            const occurrences = [];
            let searchFrom = 0;
            while (searchFrom <= text.length) {
              const foundIndex = text.indexOf(q, searchFrom);
              if (foundIndex === -1) break;
              occurrences.push(foundIndex);
              const advanceBy = Math.max(q.length, 1);
              searchFrom = foundIndex + advanceBy;
            }
            if (occurrences.length === 0) {
              const jumpSeconds = getJumpSecondsForSearchMatch(child, q, 0);
              results.push({
                sectionIndex,
                paragraphRefIndex: paragraphCounter,
                paragraphId: child.id,
                jumpSeconds,
              });
            } else {
              occurrences.forEach((indexPosition) => {
                const occurrenceIndex =
                  normalizedFirstToken && normalizedFirstToken.length > 0
                    ? countOccurrencesBeforeIndex(text, normalizedFirstToken, indexPosition)
                    : 0;
                const charJump =
                  typeof charIndexResolver === "function" && Number.isFinite(indexPosition)
                    ? charIndexResolver(indexPosition)
                    : null;
                const jumpSeconds =
                  charJump != null
                    ? charJump
                    : getJumpSecondsForSearchMatch(child, q, occurrenceIndex);
                results.push({
                  sectionIndex,
                  paragraphRefIndex: paragraphCounter,
                  paragraphId: child.id,
                  jumpSeconds,
                });
              });
            }
          }
        }
      });
    });
    setSearchResults(results);
    if (results.length > 0) {
      setSearchIndex(0);
      setSearchTarget(results[0]);
    } else {
      setSearchIndex(-1);
      setSearchTarget(null);
    }
  }, [searchQuery, filteredData, selectedSpeakers]);

  useEffect(() => {
    if (!groupByHeading) return;

    const selectedSpeakerKeys = Array.from(selectedSpeakers);   // e.g., ['Alice', 'Bob']
    const selectedHeadingLabels = Array.from(selectedHeadings); // e.g., ['Introduction', 'Conclusion']
    const headingRank = (t) => {
      if (!t) return 999;
      const m = /(\d+)/.exec(String(t));
      return m ? parseInt(m[1], 10) : 999;
    };
    const allowedSectionIdx = (() => {
      if (selectedHeadingLabels.length === 0) return null;
      const allowed = new Set();
      const meta = groupByHeading.map((section, idx) => ({
        idx,
        content: section?.heading2?.content,
        start: Number(section?.heading2?.start),
        beforeId: section?.heading2?.before_paragraph_id ?? null,
        rank: headingRank(section?.heading2?.type),
      }));
      const isFiniteStart = (v) => Number.isFinite(v);
      const isSameStart = (a, b) =>
        isFiniteStart(a) && isFiniteStart(b) && Math.abs(a - b) < 0.001;
      const hasFiniteStarts = meta.some((m) => isFiniteStart(m.start));

      for (const m of meta) {
        if (!m.content || !selectedHeadingLabels.includes(m.content)) continue;
        const baseRank = m.rank;
        const baseStart = m.start;
        const baseBeforeId = m.beforeId;
        allowed.add(m.idx);

        if (hasFiniteStarts && isFiniteStart(baseStart)) {
          // Time-based boundary.
          let boundaryEnd = null;
          for (const n of meta) {
            if (!isFiniteStart(n.start)) continue;
            if (n.start <= baseStart) continue;
            if (n.rank <= baseRank) {
              if (boundaryEnd == null || n.start < boundaryEnd) boundaryEnd = n.start;
            }
          }
          meta.forEach((n) => {
            if (!isFiniteStart(n.start)) return;
            if (n.idx === m.idx) return;
            // Same "before paragraph" anchor: include lower-level headings that precede the same speaker entry.
            if (baseBeforeId != null && n.beforeId === baseBeforeId && n.rank > baseRank) {
              allowed.add(n.idx);
              return;
            }
            if (isSameStart(n.start, baseStart) && n.rank > baseRank) {
              allowed.add(n.idx);
              return;
            }
            if (n.start > baseStart && (boundaryEnd == null || n.start < boundaryEnd) && n.rank > baseRank) {
              allowed.add(n.idx);
            }
          });
        } else {
          // Fallback: index-based boundary (order in groupByHeading).
          for (let j = m.idx + 1; j < meta.length; j++) {
            const next = meta[j];
            if (baseBeforeId != null && next.beforeId === baseBeforeId && next.rank > baseRank) {
              allowed.add(next.idx);
              continue;
            }
            if (next.rank <= baseRank) break;
            allowed.add(next.idx);
          }
        }
      }
      return allowed;
    })();

    const filtered = groupByHeading
      .filter((section, idx) => {
        if (allowedSectionIdx && !allowedSectionIdx.has(idx)) return false;
        return true;
      })
      .map((section) => {
        // Filter paragraphs by speaker name, if any speaker is selected
        const filteredChildren =
          selectedSpeakerKeys.length > 0
            ? section.children.filter((child) =>
                child?.type !== "paragraph" || selectedSpeakerKeys.includes(child.speaker_name)
              )
            : section.children;

        return {
          ...section,
          children: filteredChildren,
        };
      })
      .filter((section) => section.children.length > 0); // Remove sections with no matching content

    setFilteredData(filtered);

    // Navigation should follow filters (headings + speakers).
    const navSections = (groupByHeading || [])
      .filter((section, idx) => {
        if (allowedSectionIdx && !allowedSectionIdx.has(idx)) return false;
        return true;
      })
      .map((section) => {
      const filteredChildren =
        selectedSpeakerKeys.length > 0
          ? section.children.filter((child) =>
              child?.type !== "paragraph" || selectedSpeakerKeys.includes(child.speaker_name)
            )
          : section.children;
      return {
        ...section,
        children: filteredChildren,
      };
    });
    setNavData(navSections);
  }, [groupByHeading, selectedSpeakers, selectedHeadings]);



  useEffect(() => {
    if (id) {
      lastFetchedIdRef.current = id;
      dispatch(getFullTranscriptionByIdAsync(id));
    }
  }, [id, dispatch]);

  // Load selectedSpeakers from global store when sessionId changes
  useEffect(() => {
    // Initialize store if needed
    if (!window.selectedSpeakersStore) {
      window.selectedSpeakersStore = new Map();
    }
    
    // Load from store on mount or when sessionId changes
    if (id && prevSessionIdRef.current !== id) {
      const sessionIdStr = String(id);
      const existingSpeakers = window.selectedSpeakersStore.get(sessionIdStr);

      if (existingSpeakers && Array.isArray(existingSpeakers) && existingSpeakers.length > 0) {
        const speakersSet = new Set(existingSpeakers);
        setSelectedSpeakers(speakersSet);
        prevSelectedSpeakersRef.current = new Set(speakersSet);
      } else {
        const emptySet = new Set();
        setSelectedSpeakers(emptySet);
        prevSelectedSpeakersRef.current = new Set(emptySet);
      }
      prevSessionIdRef.current = id;
    } else if (!id && prevSessionIdRef.current !== null) {
      // If id becomes null/undefined, clear selection
      const emptySet = new Set();
      setSelectedSpeakers(emptySet);
      prevSelectedSpeakersRef.current = new Set(emptySet);
      prevSessionIdRef.current = null;
    }
  }, [id]);

  // Persist selectedSpeakers to global store whenever it changes
  useEffect(() => {
    // Don't do anything if sessionId is undefined or invalid
    if (!id || id === 'undefined' || id === 'null') {
      return;
    }
    
    // Initialize store if needed
    if (!window.selectedSpeakersStore) {
      window.selectedSpeakersStore = new Map();
    }
    
    const sessionIdStr = String(id);
    const selectedSpeakersArray = Array.from(selectedSpeakers);
    const selectedSpeakersSet = new Set(selectedSpeakers);
    
    // Always update the store to ensure persistence
    // This ensures the store is always in sync with the current state
    prevSelectedSpeakersRef.current = new Set(selectedSpeakersSet);
    window.selectedSpeakersStore.set(sessionIdStr, selectedSpeakersArray);

    // Dispatch event so other components can react if needed
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('selected-speakers-store-updated', {
        detail: { sessionId: sessionIdStr, selectedSpeakers: selectedSpeakersArray }
      }));
    }, 0);
  }, [id, selectedSpeakers]);

  if (loading && !groupByHeading) return <p>Loading transcription...</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;
  // Show loading if we don't have transcription or it doesn't match current id
  if (!fullTranscription || String(fullTranscription.id) !== String(id)) {
    return <p>Loading transcription...</p>;
  }

return (
  <div className={isEmbedded ? "h-full w-full bg-transparent" : "h-[82vh] bg-transparent rounded-2xl px-4"}>
    {groupByHeading && (
      <SessionView
        key={`transcript-${shownVersion}`}
        data={
          Array.isArray(filteredData)
            ? [
                {
                  heading2: { content: "", start: 0, type: "Heading 2" },
                  children: filteredData.flatMap((s) =>
                    (s.children || []).filter((c) => c?.type === "paragraph")
                  ),
                },
              ]
            : []
        }
        navData={navData}
        navRefresh={navRefresh}
        sessionId={id}
        //TODO MAIN
        //filename={id + (id > 46 ? "converted.mp3" : ".mp3")}
        filename={id + (id > 0 ? "converted.mp3" : ".mp3")}
        setSelectedSpeakers={setSelectedSpeakers}
        headings={headingsRegions}
        speakers={speakers}
        searchTarget={searchTarget}
        searchJumpCount={searchJumpCount}
        expandAllToken={expandAllToken}
        expandAll={areAllOpen}
        sessionStart={fullTranscription.start}
        sessionName={fullTranscription?.name || "Session"}
        filtersExpanded={filtersExpanded}
        onToggleFilters={handleToggleFilters}
        filterSpeakers={speakers}
        selectedFilterSpeakers={selectedSpeakers}
        onFilterSpeakersChange={setSelectedSpeakers}
        isActive={isActive}
        headingOptions={headingOptionsState}
        selectedHeadingFilters={selectedHeadings}
        onHeadingFilterChange={setSelectedHeadings}
        selectedGroupByKeys={selectedGroupBy}
        onGroupByChange={setSelectedGroupBy}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        onSearchPrev={handleSearchPrev}
        onSearchNext={handleSearchNext}
        searchPosition={{ index: Math.max(searchIndex, 0), total: searchResults.length }}
        onToggleAllSpeakers={handleToggleAllSpeakers}
        areAllSpeakersOpen={areAllOpen}
        onToggleSpeakersModal={() => {
          if (!readOnly) setIsSpeakersOpen(true);
        }}
        onExportClick={() => setOpenExport(true)}
        isEmbedded={isEmbedded}
        readOnly={readOnly}
        lockReason={lock}
        promptMode={promptMode}
        onPromptModeChange={setPromptMode}
      />
    )}

    <SpeakersModal
      isOpen={isSpeakersOpen}
      onClose={() => setIsSpeakersOpen(false)}
      speakers={modalSpeakers}
      onAdd={(name) => {
        if (!name || !name.trim()) return;
        // assign smallest available positive number
        const used = new Set((speakers || []).map((s) => (Number.isFinite(Number(s.value)) ? Number(s.value) : null)).filter((n) => n != null));
        let next = 1;
        while (used.has(next)) next += 1;
        setSpeakers((prev) => {
          const exists = (prev || []).some((s) => s.key === name);
          if (exists) return prev;
          return [...(prev || []), { key: name, label: name, value: next }];
        });
      }}
      onRename={async (oldKey, newName) => {
        if (!oldKey || !newName) return;
        const [oldNameRaw, oldNumRaw] = String(oldKey).split("::");
        const oldName = oldNameRaw === "no" ? "" : oldNameRaw;
        const oldNum = oldNumRaw ? Number(oldNumRaw) : undefined;
        const allParas = (fullTranscription?.paragraphs || []).filter(
          (p) => p.speaker_name === oldName && (oldNum === undefined || Number(p.speaker_number) === oldNum)
        );
        const undoBefore = allParas.map((p) => ({ ...p }));
        await Promise.all(
          allParas.map((p) =>
            dispatch(
              editParagraphAsync({
                id: p.id,
                payload: { speaker_name: newName },
              })
            )
          )
        );
        dispatch(pushUndo(buildUndoEntry({
          type: "speaker-rename",
          label: `Sprecher umbenennen (${allParas.length} Absätze)`,
          sessionId: fullTranscription?.id,
          before: undoBefore,
          after: undoBefore.map((p) => ({ ...p, speaker_name: newName })),
        })));
        // Update local state so UI reflects immediately
        setSpeakers((prev) =>
          (prev || []).map((s) =>
            s.key === oldKey || s.key === oldName
              ? { key: oldNum !== undefined ? `${newName}::${oldNum}` : newName, label: newName, value: s.value }
              : s
          )
        );
        // Update grouped data (source for navigation) optimistically
        setGroupByHeading((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((section) => ({
            ...section,
            children: (section.children || []).map((c) =>
              c?.type === "paragraph" &&
              c.speaker_name === oldName &&
              (oldNum === undefined || Number(c.speaker_number) === oldNum)
                ? { ...c, speaker_name: newName }
                : c
            ),
          }));
        });
        setFilteredData((prev) =>
          (prev || []).map((section) => ({
            ...section,
            children: (section.children || []).map((c) =>
              c?.type === "paragraph" &&
              c.speaker_name === oldName &&
              (oldNum === undefined || Number(c.speaker_number) === oldNum)
                ? { ...c, speaker_name: newName }
                : c
            ),
          }))
        );
        setNavRefresh((v) => v + 1);
      }}
      onSwap={async (keyA, keyB) => {
        if (!keyA || !keyB || keyA === keyB) return;
        const parseKey = (key) => {
          const [nameRaw, numRaw] = String(key).split("::");
          const name = nameRaw === "no" ? "" : nameRaw;
          const num = numRaw ? Number(numRaw) : undefined;
          return { name, num };
        };
        const a = parseKey(keyA);
        const b = parseKey(keyB);
        const isMatch = (p, target) =>
          p.speaker_name === target.name &&
          (target.num === undefined || Number(p.speaker_number) === target.num);

        const paragraphs = fullTranscription?.paragraphs || [];
        const aParas = paragraphs.filter((p) => isMatch(p, a));
        const bParas = paragraphs.filter((p) => isMatch(p, b));

        const undoBefore = [...aParas, ...bParas].map((p) => ({ ...p }));
        await Promise.all([
          ...aParas.map((p) =>
            dispatch(
              editParagraphAsync({
                id: p.id,
                payload: { speaker_name: b.name },
              })
            )
          ),
          ...bParas.map((p) =>
            dispatch(
              editParagraphAsync({
                id: p.id,
                payload: { speaker_name: a.name },
              })
            )
          ),
        ]);
        dispatch(pushUndo(buildUndoEntry({
          type: "speaker-swap",
          label: "Sprecher tauschen",
          sessionId: fullTranscription?.id,
          before: undoBefore,
          after: [
            ...aParas.map((p) => ({ ...p, speaker_name: b.name })),
            ...bParas.map((p) => ({ ...p, speaker_name: a.name })),
          ],
        })));

        // Update local state so UI reflects immediately
        setSpeakers((prev) =>
          (prev || []).map((s) => {
            if (s.key === a.name) return { ...s, key: b.name, label: b.name };
            if (s.key === b.name) return { ...s, key: a.name, label: a.name };
            return s;
          })
        );
        setSelectedSpeakers((prev) => {
          const next = new Set(prev || []);
          if (a.name && next.has(a.name)) {
            next.delete(a.name);
            if (b.name) next.add(b.name);
          } else if (b.name && next.has(b.name)) {
            next.delete(b.name);
            if (a.name) next.add(a.name);
          }
          return next;
        });
        const swapName = (p) => {
          if (isMatch(p, a)) return { ...p, speaker_name: b.name };
          if (isMatch(p, b)) return { ...p, speaker_name: a.name };
          return p;
        };
        setGroupByHeading((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.map((section) => ({
            ...section,
            children: (section.children || []).map((c) =>
              c?.type === "paragraph" ? swapName(c) : c
            ),
          }));
        });
        setFilteredData((prev) =>
          (prev || []).map((section) => ({
            ...section,
            children: (section.children || []).map((c) =>
              c?.type === "paragraph" ? swapName(c) : c
            ),
          }))
        );
        setNavRefresh((v) => v + 1);
      }}
    />
    <ExportModal
      isOpen={openExport}
      onClose={() => setOpenExport(false)}
      sessionName={fullTranscription?.name || "Session"}
      previewSpeaker={{ name: "Speaker", sectionTimestamp: "00-00-00" }}
      onStart={handleExportStartFromModal}
    />
  </div>
);
};

export default Session;
